/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023-2025 (original work) Open Assessment Technologies SA
 *
 */
// Stores
import { derived } from 'svelte/store';
import { user } from './auth.store.js';
import { configurationStore } from './tenantConfiguration.store.js';
// Utils
import { __ } from '@oat-sa-private/ui-core';
import { isActionAllowedForCurrentUser } from '@/core/guards/permissionsGuard.js';
import {
    FEATURE_SESSIONS,
    FEATURE_CSV_IMPORT,
    FEATURE_INDIVIDUAL_REPORT,
    FEATURE_USERS,
    FEATURE_GROUPS,
    FEATURE_STUDENT_SESSIONS,
    FEATURE_CONTENT_BANK,
    FEATURE_HIERARCHIES,
    FEATURE_LABS,
    FEATURE_ASSETS,
    FEATURE_CUSTOM_REPORTS,
    FEATURE_BATTERIES,
    FEATURE_SESSION_REPORT,
    FEATURE_ACCOUNT_SETTINGS,
    FEATURE_CREATE_PUBLIC_SESSIONS,
    FEATURE_ACCESS_PUBLIC_SESSIONS,
    FEATURE_CAMPAIGNS,
    FEATURE_SESSION_TEMPLATES,
    FEATURE_TESTS,
    FEATURE_SUPPORT,
    FEATURE_DATA_EXPLORER,
    FEATURE_CERTIFICATION_SESSIONS,
    FEATURE_DELIVERIES,
    FEATURE_IMPORT_DELIVERIES,
    FEATURE_CANDIDATES,
    FEATURE_SCORING_PROJECTS,
    FEATURE_GRADING_PROJECTS,
    FEATURE_STAFF_SESSIONS,
    FEATURE_SCORING_ASSIGNMENTS,
    FEATURE_PAPER_SESSIONS,
    FEATURE_ARCHIVED_FILES,
    FEATURE_SECURE_BROWSER,
    FEATURE_LOCALISED_DATES,
    FEATURE_DIAGNOSTIC_TOOL,
    FEATURE_ANNOUNCEMENTS,
    FEATURE_MY_ANNOUNCEMENTS,
    FEATURE_SUPERVISED_TEST_EXECUTION
} from '@/constants/features.js';

const features = [
    {
        key: FEATURE_USERS,
        isBeta: false,
        permissions: [
            { resource: 'portal.user', scopes: ['view', 'edit'] },
            { resource: 'portal.user.working-profiles', scopes: ['view'] },
            { resource: 'portal.candidate', scopes: ['view'] }
        ]
    },
    {
        key: FEATURE_GROUPS,
        isBeta: false,
        permissions: [{ resource: 'portal.group', scopes: ['view'] }]
    },
    {
        key: FEATURE_DELIVERIES,
        get name() {
            return __('Deliveries');
        },
        get description() {
            return __('Manage and organize deliveries.');
        },
        isBeta: false,
        permissions: [{ resource: 'portal.delivery', scopes: ['view'] }]
    },
    {
        key: FEATURE_IMPORT_DELIVERIES,
        get name() {
            return __('Import deliveries');
        },
        get description() {
            return __('Import Deliveries in QTI Test Package format.');
        },
        isBeta: true,
        permissions: [{ resource: 'portal.delivery', scopes: ['create', 'edit'] }]
    },
    {
        key: FEATURE_CONTENT_BANK,
        isBeta: false,
        permissions: [{ resource: 'portal.content-bank', scopes: ['view'] }]
    },
    {
        key: FEATURE_CAMPAIGNS,
        get name() {
            return __('Campaigns');
        },
        get description() {
            return __('Manage and organize campaigns.');
        },
        isBeta: false,
        permissions: [{ resource: 'portal.campaign', scopes: ['view'] }]
    },
    {
        key: FEATURE_SESSIONS,
        isBeta: false,
        permissions: [
            { resource: 'portal.session', scopes: ['view'] },
            { resource: 'portal.session', scopes: ['list'] },
            { resource: 'portal.public-session', scopes: ['view'] }
        ]
    },
    {
        key: FEATURE_STUDENT_SESSIONS,
        isBeta: false,
        permissions: [{ resource: 'portal.my-session', scopes: ['view'] }]
    },
    {
        key: FEATURE_BATTERIES,
        get name() {
            return __('Batteries');
        },
        get description() {
            return __('Manage and organize tests into batteries.');
        },
        permissions: [
            { resource: 'portal.battery', scopes: ['view'] },
            { resource: 'portal.battery', scopes: ['list'] }
        ]
    },
    {
        key: FEATURE_CSV_IMPORT,
        get name() {
            return __('Bulk data import');
        },
        get description() {
            return __('Import a spreadsheet file containing large data sets with predefined fields.');
        },
        isBeta: false,
        permissions: [{ resource: 'portal.import', scopes: ['view'] }]
    },
    {
        key: FEATURE_INDIVIDUAL_REPORT,
        get name() {
            return __('Test taker session reports');
        },
        get description() {
            return __(
                'Access test taker session reports that provide a view in to how a test taker performed in a given session.'
            );
        },
        isBeta: false,
        permissions: [{ resource: 'portal.report', scopes: ['view'] }]
    },
    {
        key: FEATURE_SESSION_REPORT,
        get name() {
            return __('Session report');
        },
        get description() {
            return __('Access session reports that provide a view into how a group performed in a given test session.');
        },
        isBeta: false,
        permissions: [{ resource: 'portal.session-report', scopes: ['view'] }]
    },
    {
        key: FEATURE_HIERARCHIES,
        permissions: [{ resource: 'portal.organization', scopes: ['view'] }]
    },
    {
        key: FEATURE_LABS,
        permissions: [{ resource: 'portal.labs', scopes: ['view'] }],
        isBeta: true,
        get name() {
            return __('Labs features');
        },
        get description() {
            return __('Access to labs features.');
        }
    },
    {
        key: FEATURE_ASSETS,
        permissions: [{ resource: 'portal.assets', scopes: ['view'] }],
        isBeta: true,
        get name() {
            return __('Assets');
        },
        get description() {
            return __('Upload and manage files.');
        }
    },
    {
        key: FEATURE_CUSTOM_REPORTS,
        permissions: [{ resource: 'portal.custom-reports', scopes: ['view'] }],
        isBeta: true,
        get name() {
            return __('Custom reports');
        }
    },
    {
        key: FEATURE_STAFF_SESSIONS,
        permissions: [{ resource: 'portal.staff-member-session-room', scopes: ['view', 'create'] }],
        isBeta: false,
        get name() {
            return __('My assigned sessions');
        }
    },
    {
        key: FEATURE_CREATE_PUBLIC_SESSIONS,
        permissions: [{ resource: 'portal.public-session', scopes: ['view', 'create'] }],
        isBeta: false,
        get name() {
            return __('Create public sessions');
        }
    },
    {
        key: FEATURE_ACCESS_PUBLIC_SESSIONS,
        permissions: [],
        isBeta: false,
        get name() {
            return __('Access public sessions');
        }
    },
    {
        key: FEATURE_ACCOUNT_SETTINGS,
        permissions: [],
        isBeta: false
    },
    {
        key: FEATURE_SESSION_TEMPLATES,
        isBeta: false,
        permissions: [{ resource: 'portal.session-template', scopes: ['view'] }],
        get name() {
            return __('Create session templates');
        }
    },
    {
        key: FEATURE_TESTS,
        get name() {
            return __('Tests');
        },
        get description() {
            return __('Allow to search and publish tests from ItemBank');
        },
        permissions: [{ resource: 'portal.tests', scopes: ['view'] }],
        isBeta: false
    },
    {
        key: FEATURE_SUPPORT,
        permissions: [{ resource: 'portal.support', scopes: ['all'] }],
        isBeta: false,
        get name() {
            return __('Support tools');
        },
        get description() {
            return __('Access to support tools.');
        }
    },
    {
        key: FEATURE_DATA_EXPLORER,
        permissions: [{ resource: 'portal.explorer', scopes: ['view'] }],
        isBeta: true,
        get name() {
            return __('Data explorer');
        },
        get description() {
            return __('Browse and consume all the information in a single interface');
        }
    },
    {
        key: FEATURE_CERTIFICATION_SESSIONS,
        permissions: [{ resource: 'portal.my-session', scopes: ['view'] }],
        isBeta: false,
        get name() {
            return __('My sessions');
        }
    },
    {
        key: FEATURE_CANDIDATES,
        permissions: [{ resource: 'portal.candidate', scopes: ['view'] }],
        isBeta: false,
        get name() {
            return __('candidates');
        }
    },
    {
        key: FEATURE_SCORING_PROJECTS,
        permissions: [{ resource: 'portal.scoring-project', scopes: ['view'] }],
        isBeta: false,
        get name() {
            return __('Scoring projects');
        }
    },
    {
        key: FEATURE_GRADING_PROJECTS,
        permissions: [{ resource: 'portal.grading-project', scopes: ['view'] }],
        isBeta: false,
        get name() {
            return __('Grading assignments');
        },
        get description() {
            return __('View and manage grading assignments');
        }
    },
    {
        key: FEATURE_SCORING_ASSIGNMENTS,
        permissions: [{ resource: 'portal.scoring-assignments', scopes: ['view'] }],
        isBeta: false,
        get name() {
            return __('Scoring assignments');
        }
    },
    {
        key: FEATURE_PAPER_SESSIONS,
        isBeta: false,
        permissions: [
            { resource: 'portal.paper-scans', scopes: ['view'] },
            { resource: 'portal.paperbased-session', scopes: ['view'] }
        ]
    },
    {
        key: FEATURE_ARCHIVED_FILES,
        isBeta: false,
        permissions: [{ resource: 'portal.archive-files', scopes: ['view'] }]
    },
    {
        key: FEATURE_SECURE_BROWSER,
        isBeta: false,
        permissions: [{ resource: 'portal.system-settings', scopes: ['edit'] }],
        get name() {
            return __('TAO Secure Browser');
        },
        get description() {
            return __('Enable TAO Secure Browser for enhanced test security');
        }
    },
    {
        key: FEATURE_LOCALISED_DATES,
        isBeta: false,
        permissions: [{ resource: 'portal.system-settings', scopes: ['edit'] }],
        get name() {
            return __('Localised dates');
        },
        get description() {
            return __('Display dates using locale-specific formats configured for the tenant');
        }
    },
    {
        key: FEATURE_DIAGNOSTIC_TOOL,
        isBeta: false,
        permissions: [{ resource: 'portal.system-settings', scopes: ['edit'] }],
        get name() {
            return __('Diagnostic Tool');
        },
        get description() {
            return __('Configure Diagnostic Tool options.');
        }
    },
    {
        key: FEATURE_ANNOUNCEMENTS,
        isBeta: false,
        permissions: [
            { resource: 'portal.announcements', scopes: ['view'] },
            { resource: 'portal.announcements', scopes: ['publish'] }
        ]
    },
    {
        key: FEATURE_MY_ANNOUNCEMENTS,
        isBeta: false,
        permissions: [{ resource: 'portal.announcements', scopes: ['publish'] }]
    },
    {
        key: FEATURE_SUPERVISED_TEST_EXECUTION,
        isBeta: false,
        permissions: []
    }
];

/**
 * @typedef {Object} Feature
 * @property {string} key
 * @property {string} name
 * @property {boolean} isBeta
 * @property {string[]} roles
 */

const featuresRegistryStore = derived([configurationStore], ([$tenantConfiguration]) => {
    const tenantFeatures = $tenantConfiguration?.features ?? [];
    return features.filter(feature => tenantFeatures.includes(feature.key));
});

export const availableFeaturesStore = derived([featuresRegistryStore, user], ([$featuresRegistry]) =>
    $featuresRegistry.filter(feature => isActionAllowedForCurrentUser(feature.permissions ?? []))
);

// non-beta features are enabled by default for appropriate role
export const enabledFeaturesStore = derived([user, availableFeaturesStore], ([$user, $availableFeatures]) => [
    ...$availableFeatures.filter(item => !item.isBeta).map(item => item.key),
    ...($user?.features ?? []).filter(featureKey => {
        const feature = features.find(({ key, isBeta }) => key === featureKey && isBeta);
        return feature && isActionAllowedForCurrentUser(feature.permissions);
    })
]);
export const availableBetaFeaturesStore = derived(availableFeaturesStore, $availableFeatures =>
    $availableFeatures.filter(item => item.isBeta)
);

export const featuresStore = createFeaturesStore();

function createFeaturesStore() {
    return derived(
        [availableBetaFeaturesStore, enabledFeaturesStore],
        ([$availableBetaFeatures, $enabledFeatures]) => ({
            isFeatureBeta: featureKey => $availableBetaFeatures.map(({ key }) => key).includes(featureKey),
            isFeatureEnabled: featureKey => $enabledFeatures.includes(featureKey)
        })
    );
}
